import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Leaf, Sprout, TreePine, Recycle, Sun, HandHeart, Clock, DollarSign, Users, Repeat, Send, Loader2, HeartHandshake, Trophy, MapPin, Sparkles } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { money } from "@/lib/format";

import RichContent from "@/components/RichContent";
const HERO = "https://static.prod-images.emergentagent.com/jobs/cc147483-69b1-437e-8d93-352c289a737f/images/246d18e6b59c8e4aa5d3ec5b249a4662404a021f6baeca115a85cba2e3e154dc.png";
const ICONS = [TreePine, Sprout, Recycle, HandHeart, Leaf];
const TYPE_META = {
  volunteer: { label: "Volunteer", cls: "bg-[#5C8A72]/15 text-[#5C8A72]", icon: HandHeart },
  paid: { label: "Paid", cls: "bg-[#144F75]/10 text-[#144F75]", icon: DollarSign },
  donation: { label: "Donation", cls: "bg-[#D98E73]/15 text-[#B5794E]", icon: HeartHandshake },
  trade: { label: "Trade", cls: "bg-[#8A6FB0]/15 text-[#6F569B]", icon: Repeat },
};
const FILTERS = [["", "All"], ["volunteer", "Volunteer"], ["paid", "Paid"], ["donation", "Donation"], ["trade", "Trade"]];

export default function Stewardship() {
  const { user } = useAuth();
  const [view, setView] = useState("opportunities");
  const [filter, setFilter] = useState("");
  const [applyOp, setApplyOp] = useState(null);

  const { data: projects = [] } = useQuery({ queryKey: ["stewardship"], queryFn: () => api.get("/stewardship").then((r) => r.data) });
  const { data: opportunities = [] } = useQuery({ queryKey: ["steward-ops", filter], queryFn: () => api.get(`/stewardship/opportunities${filter ? `?type=${filter}` : ""}`).then((r) => r.data) });
  const { data: impact } = useQuery({ queryKey: ["steward-impact"], queryFn: () => api.get("/stewardship/impact").then((r) => r.data) });

  return (
    <div data-testid="stewardship-page">
      <Seo
        title="Stewardship & Earth Care"
        description="Regenerative land care, ethical business, volunteerism, and planetary wellness. Explore paid, volunteer, donation, and trade stewardship opportunities."
        path="/stewardship"
      />
      <PageHero label="Stewardship" title="Enhancing Your World enhances our world."
        subtitle="Sometimes we need a hand, sometimes we get to give one — volunteer, work, give, or trade through regenerative projects and community care."
        image={HERO} testid="stewardship-hero" />

      {/* Impact metrics */}
      {impact && (
        <div className="bg-[#144F75] text-white" data-testid="stewardship-impact">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
            <Metric icon={Users} value={impact.volunteers} label="Volunteers" />
            <Metric icon={Clock} value={`${impact.hours_logged}`} label="Hours given" />
            <Metric icon={HeartHandshake} value={`${money(impact.funds_raised)}`} label="Funds raised" />
            <Metric icon={Trophy} value={impact.contributions_completed} label="Contributions done" />
          </div>
        </div>
      )}

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        {/* View toggle */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div className="flex gap-2">
            <button onClick={() => setView("opportunities")} data-testid="steward-view-ops" className={`px-4 py-2 rounded-full text-sm ${view === "opportunities" ? "bg-[#144F75] text-white" : "bg-[#F1EDE6] text-[#4A5D6B]"}`}>Opportunities</button>
            {user && <button onClick={() => setView("mine")} data-testid="steward-view-mine" className={`px-4 py-2 rounded-full text-sm ${view === "mine" ? "bg-[#144F75] text-white" : "bg-[#F1EDE6] text-[#4A5D6B]"}`}>My Stewardship</button>}
          </div>
        </div>

        {view === "opportunities" && (
          <>
            <div className="flex flex-wrap gap-2 mb-8">
              {FILTERS.map(([v, l]) => (
                <button key={v} onClick={() => setFilter(v)} data-testid={`steward-filter-${v || "all"}`}
                  className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filter === v ? "bg-[#D98E73] text-white border-[#D98E73]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#D98E73]"}`}>{l}</button>
              ))}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {opportunities.map((o) => {
                const m = TYPE_META[o.opportunity_type] || TYPE_META.volunteer;
                const pct = o.opportunity_type === "donation" && o.donation_goal > 0 ? Math.min(100, Math.round((o.donation_raised / o.donation_goal) * 100)) : null;
                return (
                  <div key={o.id} className="eyw-card overflow-hidden flex flex-col" data-testid={`steward-op-${o.id}`}>
                    {o.image && <img src={o.image} alt={o.title} className="h-40 w-full object-cover" />}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${m.cls}`}><m.icon size={12} /> {m.label}</span>
                        {o.status === "filled" && <span className="text-xs px-2 py-0.5 rounded-full bg-[#E5E0D8] text-[#4A5D6B]">Full</span>}
                      </div>
                      <h3 className="font-serif text-xl text-[#1A242B]">{o.title}</h3>
                      <RichContent text={o.description} className="text-sm text-[#4A5D6B] mt-2 flex-1" />
                      <div className="text-xs text-[#8FA89B] mt-3 space-y-1">
                        {o.location && <p className="flex items-center gap-1.5"><MapPin size={12} /> {o.location}</p>}
                        {o.commitment && <p className="flex items-center gap-1.5"><Clock size={12} /> {o.commitment}</p>}
                        {o.compensation && <p className="flex items-center gap-1.5"><Sparkles size={12} /> {o.compensation}</p>}
                        {o.spots > 0 && <p className="flex items-center gap-1.5"><Users size={12} /> {o.filled}/{o.spots} spots filled</p>}
                      </div>
                      {pct !== null && (
                        <div className="mt-3">
                          <div className="h-2 rounded-full bg-[#E5E0D8] overflow-hidden"><div className="h-full bg-[#D98E73]" style={{ width: `${pct}%` }} /></div>
                          <p className="text-xs text-[#8FA89B] mt-1">{money(o.donation_raised)} of {money(o.donation_goal)}</p>
                        </div>
                      )}
                      <button onClick={() => setApplyOp(o)} disabled={o.status === "filled"} data-testid={`steward-apply-btn-${o.id}`}
                        className="eyw-btn !py-2 text-sm mt-4 disabled:opacity-50">
                        <Send size={14} /> {o.opportunity_type === "donation" ? "Donate" : o.opportunity_type === "paid" ? "Apply" : "Get involved"}
                      </button>
                    </div>
                  </div>
                );
              })}
              {opportunities.length === 0 && <p className="text-[#4A5D6B] text-sm">No opportunities in this category right now.</p>}
            </div>
          </>
        )}

        {view === "mine" && user && <MyStewardship />}

        {/* Impact goals (regenerative projects) */}
        {projects.length > 0 && (
          <div className="mt-20">
            <h2 className="font-serif text-3xl text-[#1A242B] mb-6">Our impact goals</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {projects.map((p, i) => {
                const Icon = ICONS[i % ICONS.length];
                const pct = p.goal > 0 ? Math.min(100, Math.round((p.current / p.goal) * 100)) : 0;
                return (
                  <div key={p.id} data-testid={`stewardship-${i}`} className="eyw-card p-8">
                    <div className="flex items-start gap-4">
                      <span className="grid place-items-center h-12 w-12 rounded-full bg-[#144F75]/10 text-[#144F75] shrink-0"><Icon size={22} /></span>
                      <div className="flex-1">
                        <h3 className="text-xl text-[#1A242B] mb-1 flex items-center gap-2">{p.title}
                          {p.needs_volunteers && <span className="text-[10px] uppercase tracking-wide bg-[#D98E73]/15 text-[#B5794E] px-2 py-0.5 rounded-full">needs volunteers</span>}</h3>
                        <RichContent text={p.description} className="text-[#4A5D6B] text-sm leading-relaxed" />
                        <div className="mt-4"><div className="h-2 rounded-full bg-[#E5E0D8] overflow-hidden"><div className="h-full bg-[#144F75] rounded-full transition-all" style={{ width: `${pct}%` }} /></div>
                          <p className="text-xs text-[#8FA89B] mt-1">{pct}% toward goal</p></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-16 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center rounded-3xl bg-[#144F75] p-10 lg:p-14">
          <div>
            <Sun className="text-[#D98E73] mb-4" size={30} />
            <h2 className="font-serif text-3xl lg:text-4xl text-white leading-tight">Human enhancement and planetary care are one practice.</h2>
            <p className="text-white/80 mt-4">As we become more whole, present, and responsible, we steward life more consciously. Join a stewardship project and participate in the healing of place.</p>
          </div>
          <div className="flex flex-col gap-3">
            <Link to="/join" className="eyw-btn !bg-[#D98E73] hover:!bg-[#c97d63] justify-center">Join the Ecosystem</Link>
            <Link to="/community" className="eyw-btn-ghost !border-white !text-white hover:!bg-white hover:!text-[#144F75] justify-center">Stewardship Group</Link>
          </div>
        </div>
      </section>

      {applyOp && <ApplyModal op={applyOp} user={user} onClose={() => setApplyOp(null)} />}
    </div>
  );
}

function Metric({ icon: Icon, value, label }) {
  return (
    <div className="text-center" data-testid={`steward-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={20} className="text-[#D98E73] mx-auto mb-2" />
      <div className="font-serif text-3xl">{value}</div>
      <div className="text-xs text-white/70 mt-1">{label}</div>
    </div>
  );
}

function ApplyModal({ op, user, onClose }) {
  const qc = useQueryClient();
  const [message, setMessage] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const isDonation = op.opportunity_type === "donation";
  const submit = async () => {
    if (!user) { toast.error("Please sign in to get involved."); return; }
    if (isDonation && (!amount || Number(amount) <= 0)) { toast.error("Enter a donation amount"); return; }
    setSaving(true);
    try {
      if (isDonation) {
        const { data } = await api.post(`/stewardship/opportunities/${op.id}/donate`, { amount: Number(amount), message, origin_url: window.location.origin });
        if (data.url) { window.location.href = data.url; return; }
      } else {
        await api.post(`/stewardship/opportunities/${op.id}/apply`, { message, offered_amount: 0 });
        toast.success("Application submitted!");
      }
      qc.invalidateQueries({ queryKey: ["steward-ops"] });
      qc.invalidateQueries({ queryKey: ["my-steward-apps"] });
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="steward-apply-modal">
        <h3 className="font-serif text-xl text-[#1A242B] mb-1">{isDonation ? "Donate to" : "Get involved with"} {op.title}</h3>
        {!user && <p className="text-sm text-[#B5794E] mb-3">You'll need to <Link to="/login" className="underline">sign in</Link> first.</p>}
        {isDonation && (
          <div className="mb-3"><label className="text-sm font-medium mb-1 block">Donation amount ($)</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="steward-pledge-amount" className="eyw-input w-full" placeholder="50" />
            <p className="text-xs text-[#8FA89B] mt-1">You'll be taken to secure Stripe checkout.</p></div>
        )}
        <label className="text-sm font-medium mb-1 block">Message {isDonation ? "(optional)" : ""}</label>
        <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="steward-apply-message" className="eyw-input w-full resize-none" placeholder="Tell us a little about you…" />
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          <button onClick={submit} disabled={saving || !user} data-testid="steward-apply-submit" className="eyw-btn !py-2 text-sm disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={15} />} Submit</button>
        </div>
      </div>
    </div>
  );
}

function MyStewardship() {
  const qc = useQueryClient();
  const { data: apps = [] } = useQuery({ queryKey: ["my-steward-apps"], queryFn: () => api.get("/stewardship/me/applications").then((r) => r.data) });
  const { data: mine } = useQuery({ queryKey: ["my-steward-impact"], queryFn: () => api.get("/stewardship/me/impact").then((r) => r.data) });
  const withdraw = async (id) => {
    if (!window.confirm("Withdraw this application?")) return;
    try { await api.delete(`/stewardship/me/applications/${id}`); toast.success("Withdrawn"); qc.invalidateQueries({ queryKey: ["my-steward-apps"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const badge = (s) => s === "accepted" || s === "completed" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : s === "declined" ? "bg-red-100 text-red-600" : "bg-[#D98E73]/15 text-[#B5794E]";
  return (
    <div data-testid="my-stewardship">
      {mine && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="eyw-card p-5"><div className="font-serif text-2xl text-[#144F75]">{mine.hours_logged}</div><p className="text-xs text-[#4A5D6B]">Hours given</p></div>
          <div className="eyw-card p-5"><div className="font-serif text-2xl text-[#144F75]">{money(mine.donated)}</div><p className="text-xs text-[#4A5D6B]">Donated</p></div>
          <div className="eyw-card p-5"><div className="font-serif text-2xl text-[#144F75]">{mine.opportunities}</div><p className="text-xs text-[#4A5D6B]">Opportunities</p></div>
          <div className="eyw-card p-5"><div className="font-serif text-2xl text-[#144F75]">{mine.completed}</div><p className="text-xs text-[#4A5D6B]">Completed</p></div>
        </div>
      )}
      <div className="eyw-card divide-y divide-[#E5E0D8]">
        {apps.map((a) => (
          <div key={a.id} className="p-4 flex items-center justify-between gap-3" data-testid={`my-steward-app-${a.id}`}>
            <div>
              <div className="text-sm font-semibold text-[#1A242B]">{a.opportunity_title}</div>
              <div className="text-xs text-[#4A5D6B]">{a.opportunity_type}{a.offered_amount > 0 && ` · ${money(a.offered_amount)}`}{a.hours_logged > 0 && ` · ${a.hours_logged}h logged`}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2.5 py-1 rounded-full ${badge(a.status)}`}>{a.status}</span>
              {a.status === "pending" && <button onClick={() => withdraw(a.id)} data-testid={`my-steward-withdraw-${a.id}`} className="text-xs text-red-500 hover:text-red-600">Withdraw</button>}
            </div>
          </div>
        ))}
        {apps.length === 0 && <p className="text-[#4A5D6B] text-sm p-4">You haven't applied to any opportunities yet.</p>}
      </div>
    </div>
  );
}
